import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, MapPin, CheckCircle, Warning, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const EMPTY = { label: "", street_line1: "", street_line2: "", suburb: "", state: "NSW", postcode: "", contact_name: "", contact_phone: "", business_hours: "" };

export default function OrgAddressesPage() {
  const { activeOrg, checkAuth } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchOrg = useCallback(async () => {
    if (!activeOrg) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orgs/${activeOrg.id}`, { withCredentials: true });
      setOrg(data);
    } catch { toast.error("Failed to load addresses"); }
    setLoading(false);
  }, [activeOrg]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (a) => { setEditingId(a.id); setForm({ ...EMPTY, ...a }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.street_line1 || !form.suburb || !form.postcode) { toast.error("Address line, suburb and postcode required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/orgs/${activeOrg.id}/addresses/${editingId}`, form, { withCredentials: true });
        toast.success("Address updated — awaiting verification");
      } else {
        await axios.post(`${API}/orgs/${activeOrg.id}/addresses`, form, { withCredentials: true });
        toast.success("Address submitted — awaiting Amcal verification");
      }
      setShowDialog(false);
      fetchOrg();
      checkAuth();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete address "${a.label || a.street_line1}"?`)) return;
    try {
      await axios.delete(`${API}/orgs/${activeOrg.id}/addresses/${a.id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchOrg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!activeOrg) return <div className="max-w-7xl mx-auto px-4 py-8"><p className="text-zinc-500">No active organization</p></div>;

  const addresses = org?.addresses || [];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="org-addresses-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{activeOrg.name}</p>
          <h1 className="font-[Chivo] text-3xl font-black tracking-tighter text-zinc-950 mt-1">Delivery Addresses</h1>
          <p className="text-sm text-zinc-500 mt-1">{addresses.length} address{addresses.length === 1 ? "" : "es"} · only verified addresses can receive orders</p>
        </div>
        <button onClick={openAdd} className="px-4 py-2 text-sm font-semibold bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center gap-2" data-testid="add-address-btn">
          <Plus size={16} weight="bold" /> Add Address
        </button>
      </div>

      {loading ? <p className="font-mono text-sm text-zinc-500 text-center py-12">LOADING...</p> : (
        <div className="space-y-3" data-testid="addresses-list">
          {addresses.map(a => (
            <div key={a.id} className={`border p-4 ${a.verified ? "border-zinc-200" : "border-amber-300 bg-amber-50/30"}`} data-testid={`address-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className={a.verified ? "text-emerald-600" : "text-amber-600"} weight={a.verified ? "fill" : "regular"} />
                  <div>
                    {a.label && <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">{a.label}</p>}
                    <p className="text-sm font-medium text-zinc-950">{a.street_line1}{a.street_line2 ? `, ${a.street_line2}` : ""}</p>
                    <p className="text-sm text-zinc-700">{a.suburb}, {a.state} {a.postcode}</p>
                    {a.contact_name && <p className="text-xs text-zinc-500 mt-1">Contact: {a.contact_name} {a.contact_phone && `· ${a.contact_phone}`}</p>}
                    {a.business_hours && <p className="text-xs text-zinc-500">Hours: {a.business_hours}</p>}
                    <div className="mt-2">
                      {a.verified ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle size={10} weight="fill" /> Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200">
                          <Warning size={10} /> Awaiting Amcal verification
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(a)} className="p-1.5 text-zinc-400 hover:text-[#0055FF]" data-testid={`edit-addr-${a.id}`}><PencilSimple size={16} /></button>
                  {addresses.length > 1 && (
                    <button onClick={() => handleDelete(a)} className="p-1.5 text-zinc-400 hover:text-red-500" data-testid={`delete-addr-${a.id}`}><Trash size={16} /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {addresses.length === 0 && <p className="text-sm text-zinc-500 text-center py-12">No addresses yet</p>}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="address-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">{editingId ? "Edit Address" : "Add Address"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">New or edited addresses require Amcal verification before they can be used for orders.</div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Label (optional)</Label><Input value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder="e.g. Manly Branch" className="mt-1.5 rounded-none" data-testid="addr-label-input" /></div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Street Address</Label><Input value={form.street_line1} onChange={e => setForm({...form, street_line1: e.target.value})} required className="mt-1.5 rounded-none" data-testid="addr-line1-input" /></div>
            <Input value={form.street_line2} onChange={e => setForm({...form, street_line2: e.target.value})} placeholder="Unit / floor (optional)" className="rounded-none" data-testid="addr-line2-input" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Suburb</Label><Input value={form.suburb} onChange={e => setForm({...form, suburb: e.target.value})} required className="mt-1.5 rounded-none" data-testid="addr-suburb-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">State</Label>
                  <Select value={form.state} onValueChange={v => setForm({...form, state: v})}>
                    <SelectTrigger className="mt-1.5 rounded-none" data-testid="addr-state-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Postcode</Label><Input value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} required maxLength={4} className="mt-1.5 rounded-none" data-testid="addr-postcode-input" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} className="mt-1.5 rounded-none" data-testid="addr-contact-name-input" /></div>
              <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Contact Phone</Label><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="mt-1.5 rounded-none" data-testid="addr-contact-phone-input" /></div>
            </div>
            <div><Label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Business Hours (optional)</Label><Input value={form.business_hours} onChange={e => setForm({...form, business_hours: e.target.value})} placeholder="e.g. Mon-Fri 9am-5pm" className="mt-1.5 rounded-none" data-testid="addr-hours-input" /></div>
            <button onClick={handleSave} className="w-full bg-[#0055FF] text-white font-semibold py-3 hover:bg-[#0044CC]" data-testid="save-address-btn">{editingId ? "UPDATE" : "ADD"} ADDRESS</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
